import React, { useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Button, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { PopoverFilterEntry } from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import {
  EventRepository,
  EventTypes,
  ParsedEvent,
  getEventACHType,
  getEventIcon,
  getEventRoute,
  getEventStatus,
  getEventSummary,
  getRawEventDirection,
  getRawEventType,
  parseEvent,
} from '~/repositories';
import { EventEvent } from '~/typings/API';

type EventsTableRow = ParsedEvent & { action: React.ReactElement };

export const JSONButton: React.FC<React.ComponentPropsWithoutRef<'button'>> = (props) => (
  <Button type="button" variant="secondary" size="xs" {...props} />
);

export const EventTypeCell: React.FC<{ data: ParsedEvent['data'] }> = ({ data }) => {
  const eventType = data?.eventType ?? '';

  return (
    <div className="-my-1 flex grow flex-col justify-center gap-1">
      <h4 className="m-0 p-0 text-sm leading-5 font-medium text-foreground capitalize">
        {getRawEventDirection(eventType)} {getRawEventType(eventType)} {getEventACHType(data)}
      </h4>
      <div className="flex items-center gap-1 text-xs leading-4 font-medium text-muted-foreground">
        <span className="-ml-0.5 flex items-center [--icon-size-x:16px] [--icon-size-y:16px]">
          {getEventIcon(eventType)}
        </span>
        <span>{getEventSummary(data)}</span>
      </div>
    </div>
  );
};

const eventTypes = Object.values(EventTypes).filter((name: string) => !['UNRECOGNIZED'].includes(name));

const filter: PopoverFilterEntry[] = [
  {
    id: 'types',
    label: 'Type',
    type: 'select',
    list: eventTypes.map((eventType) => ({
      label: eventType,
      value: eventType,
    })),
  },
  {
    id: 'created',
    label: 'Created',
    type: 'date',
  },
];

interface EventsTableProps {
  tableId: string;
  fetchEvents: (params: any) => Promise<{ events: EventEvent[]; hasMore: boolean }>;
  emptyTitle: string;
}

export const EventsTable: React.FC<EventsTableProps> = ({ tableId, fetchEvents, emptyTitle }) => {
  const navigate = useNavigate();
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);
  const { timeFormat, onTimeFormatChange } = useTimeFormat(tableId);

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    const response = await fetchEvents(params);
    const entries = response.events.map(
      (event): EventsTableRow => ({
        ...parseEvent(event),
        action: (
          <div className="flex items-center justify-end">
            <JSONButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </div>
        ),
      })
    );

    return { entries, hasMore: response.hasMore };
  };

  const columns: DataTableColumnDef<EventsTableRow>[] = [
    {
      header: 'Type',
      accessor: 'data',
      width: 'minmax(300px, auto)',
      cell: ({ value }) => <EventTypeCell data={value} />,
    },
    {
      header: 'Status',
      accessor: 'eventType',
      width: 'minmax(min-content, 100px)',
      cell: ({ value }) => getEventStatus(value),
    },
    getTimestampColumn<EventsTableRow>({ accessor: 'createdAt', timeFormat, onTimeFormatChange }),
    {
      header: '',
      width: 'min-content',
      accessor: 'action',
      cell: ({ value }) => value,
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <PaginatedDataTable<EventsTableRow>
        tableId={tableId}
        fetch={handleFetch}
        filter={filter}
        empty={{
          icon: <Icon.AnimationSignal />,
          title: emptyTitle,
          description: 'Create your first person or a business entity.',
          action: (
            <Button size="sm" onClick={() => navigate(`${ROUTE.ENTITIES}/edit/person`)}>
              <Icon.User />
              Create Entity
            </Button>
          ),
        }}
        columns={columns}
        rowClassName="min-h-11 py-1"
        onRowClick={(row) =>
          navigate(
            getRawEventType(row.eventType) === 'Identity'
              ? `${getEventRoute(row.eventType)}/${(row.data.subType ?? 'undefined').toLowerCase()}/${row.data.id}`
              : getRawEventType(row.eventType) === undefined
                ? ''
                : `${getEventRoute(row.eventType)}/${row.data.id}`
          )
        }
      />
    </>
  );
};

export const PageEvents: React.FC = () => (
  <EventsTable tableId="events" fetchEvents={EventRepository.getAll} emptyTitle="No events found" />
);
